// W18 / M18 — Projektlista · W20 / M20 — Projekt részletei.

import { t } from '../lib/i18n';
import { avatar, avatarStack, emptyState } from '../lib/components';
import { h, icon, icons } from '../lib/dom';
import type { PersonView, ProjectView } from '../lib/data';
import { formatDeadline, formatElapsedClock, type TaskView } from '../lib/model';
import { denseTaskTable } from './tasks';

export type ProjectFilterKind = 'all' | 'active' | 'recurring' | 'closed';

/// Egy projekt akkor lezárt, ha minden feladata lezárult.
export function isProjectClosed(project: ProjectView): boolean {
  if (project.tasks.length === 0) return false;
  return project.tasks.every((t) => t.status === 'completed' || t.status === 'failed');
}

export function completedCount(project: ProjectView): number {
  return project.tasks.filter((t) => t.status === 'completed').length;
}

export function progressOf(project: ProjectView): number {
  return project.tasks.length === 0 ? 0 : completedCount(project) / project.tasks.length;
}

/// A projekt legkorábbi nyitott határideje — ez a "Határidő" a kártyán.
export function nextDeadline(project: ProjectView): Date | undefined {
  const open = project.tasks
    .filter((t) => t.deadline && t.status !== 'completed' && t.status !== 'failed')
    .map((t) => t.deadline!)
    .sort((a, b) => a.getTime() - b.getTime());
  return open[0];
}

export function filterProjects(
  projects: ProjectView[],
  kind: ProjectFilterKind,
): ProjectView[] {
  switch (kind) {
    case 'active':
      return projects.filter((p) => !isProjectClosed(p));
    case 'recurring':
      return projects.filter((p) => p.isPeriodic);
    case 'closed':
      return projects.filter((p) => isProjectClosed(p));
    case 'all':
      return projects;
  }
}

function progressBar(ratio: number): HTMLElement {
  const percent = Math.round(ratio * 100);
  return h(
    'div',
    {
      class: 'prog',
      role: 'progressbar',
      'aria-valuenow': String(percent),
      'aria-valuemin': '0',
      'aria-valuemax': '100',
      'aria-label': t('project.percentDone', [String(percent)]),
    },
    h('i', { style: { width: `${percent}%` } }),
  );
}

/// A projekt lezárásának időpontja: a legkésőbbi lezárt feladat határideje.
///
/// A projekten nincs külön „lezárva" mező — az állapot a feladataiból
/// következik, tehát a dátum is onnan jöhet.
function closedAt(project: ProjectView): Date | undefined {
  const dates = project.tasks
    .map((task) => task.finishedAt)
    .filter((date): date is Date => date !== undefined)
    .sort((a, b) => b.getTime() - a.getTime());
  return dates[0];
}

/// Rövid dátum a kártyára („10. 15."), év nélkül a folyó évben.
function shortDate(date: Date, now = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  const base = `${pad(date.getMonth() + 1)}. ${pad(date.getDate())}.`;
  return date.getFullYear() === now.getFullYear() ? base : `${date.getFullYear()}. ${base}`;
}

function projectCard(
  project: ProjectView,
  people: PersonView[],
  onOpen: (project: ProjectView) => void,
): HTMLElement {
  const done = completedCount(project);
  const total = project.tasks.length;
  const deadline = nextDeadline(project);
  const closed = isProjectClosed(project);
  const percent = Math.round(progressOf(project) * 100);

  const participantNames = [...new Set(project.tasks.flatMap((t) => t.assigneeIds))].map(
    (id) => people.find((p) => p.id === id)?.fullName ?? 'Ismeretlen',
  );

  const closedDate = closed ? closedAt(project) : undefined;
  const overdue = !closed && deadline !== undefined && deadline.getTime() < Date.now();

  /// A terv három változatot mutat: „Határidő: 10. 15.", ismétlődőnél
  /// „Következő: 10. 01.", lezártnál „Lezárva: 09. 20."
  const dateLabel = closed
    ? closedDate
      ? t('project.closedOn', [shortDate(closedDate)])
      : t('project.closed')
    : deadline
      ? (project.isPeriodic ? t('project.nextOn') : t('project.deadlineOn')).replace(
          '%s',
          shortDate(deadline),
        )
      : t('project.noDeadline');

  return h(
    'button',
    {
      type: 'button',
      class: 'card pad16 col g12',
      style: { width: '100%', textAlign: 'left', font: 'inherit', cursor: 'pointer' },
      onClick: () => onOpen(project),
    },
    // Fejléc: cím + alcím balra, az „Ismétlődő" jelvény jobbra fent.
    h(
      'span',
      { class: 'row between g10', style: { alignItems: 'flex-start' } },
      h(
        'span',
        { class: 'col', style: { gap: '3px', minWidth: '0' } },
        h('span', { class: 'body w6', text: project.title }),
        h('span', {
          class: 'caption',
          // Ismetlodonel a terv az UTEMET irja ki, nem a darabszamokat.
          text:
            project.isPeriodic && project.recurrenceText
              ? project.recurrenceText
              : t('project.tasksAndParticipants', [
                  String(total),
                  String(participantNames.length),
                ]),
        }),
      ),
      project.isPeriodic
        ? h(
            'span',
            { class: 'badge st-prog', style: { height: '21px', fontSize: '11px', flex: 'none' } },
            h('span', { class: 'ic', style: { width: '11px', height: '11px' } }, icon(icons.timer, 11)),
            h('span', { text: t('project.recurring') }),
          )
        : null,
    ),
    // Haladas: balra a darabszam, jobbra a szazalek, alattuk a sav.
    h(
      'span',
      { class: 'col', style: { gap: '6px' } },
      h(
        'span',
        { class: 'row between' },
        h('span', {
          class: 'caption',
          text: t('project.doneOfTotal', [String(done), String(total)]),
        }),
        h('span', { class: 'caption mono w6', text: `${percent}%` }),
      ),
      progressBar(progressOf(project)),
    ),
    // Lablec: resztvevok balra, datum jobbra.
    h(
      'span',
      { class: 'row between g8', style: { alignItems: 'center' } },
      participantNames.length > 0
        ? avatarStack(participantNames, 3)
        : h('span', { class: 'caption tm', text: t('project.noParticipant') }),
      h(
        'span',
        {
          class: overdue ? 'caption overdue' : 'caption',
          style: { display: 'inline-flex', alignItems: 'center', gap: '4px' },
        },
        h('span', { class: 'ic', style: { width: '12px', height: '12px' } }, icon(icons.calendar, 12)),
        h('span', { text: dateLabel }),
      ),
    ),
  );
}

export function projectListPage(options: {
  projects: ProjectView[];
  people: PersonView[];
  kind: ProjectFilterKind;
  onKindChange: (kind: ProjectFilterKind) => void;
  onOpen: (project: ProjectView) => void;
  onNewProject: () => void;
}): HTMLElement {
  const kinds: { key: ProjectFilterKind; label: string }[] = [
    { key: 'active', label: t('project.tabActive') },
    { key: 'recurring', label: t('project.recurring') },
    { key: 'closed', label: t('project.tabClosed') },
  ];

  const filtered = filterProjects(options.projects, options.kind);

  return h(
    'div',
    { class: 'col', style: { gap: '0' } },
    // A terv szurosavja — ugyanaz a minta, mint a feladatlistan (W05):
    // felulet-hatter, chipek, jobbra a torles.
    h(
      'div',
      {
        class: 'row g8 wrap',
        style: {
          padding: '10px 16px',
          background: 'var(--surface)',
          borderBottom: '1px solid var(--border)',
          alignItems: 'center',
        },
      },
      h(
        'span',
        { class: 'row g6', style: { alignItems: 'center' } },
        h('span', { class: 'ic', style: { width: '14px', height: '14px' } }, icon(icons.filter, 14)),
        h('span', { class: 'body-sm w6', text: t('filter.shortTitle') }),
      ),
      ...kinds.map((k) => {
        const on = options.kind === k.key;
        return h(
          'button',
          {
            type: 'button',
            class: `chip${on ? ' on' : ''}`,
            'aria-pressed': on ? 'true' : 'false',
            style: { font: 'inherit', cursor: 'pointer' },
            onClick: () => options.onKindChange(on ? 'all' : k.key),
          },
          h('span', { text: k.label }),
          on ? h('span', { class: 'x', 'aria-hidden': 'true', text: '×' }) : null,
        );
      }),
      options.kind !== 'all'
        ? h('button', {
            class: 'btn btn-ghost btn-sm',
            type: 'button',
            style: { marginLeft: 'auto' },
            text: t('filter.clear'),
            onClick: () => options.onKindChange('all'),
          })
        : h('span', { class: 'grow' }),
      h('button', {
        class: 'btn btn-primary btn-sm',
        type: 'button',
        text: t('task.newProjectTitle'),
        onClick: options.onNewProject,
      }),
    ),
    filtered.length === 0
      ? emptyState({
          title: 'Nincsenek projektek',
          text: t('project.noneOnThisFilter'),
          actionLabel: t('task.newProjectTitle'),
          onAction: options.onNewProject,
        })
      : h(
          'div',
          // A terv harom oszlopot mutat; kettonel a kartyak feleslegesen
          // szelesek lennenek ahhoz a keves adathoz, amit tartalmaznak.
          { class: 'grid c3', style: { padding: '20px' } },
          ...filtered.map((project) => projectCard(project, options.people, options.onOpen)),
        ),
  );
}

// ---------------------------------------------------------------------------
// W20 — Projekt részletei
// ---------------------------------------------------------------------------

export function projectDetailPage(options: {
  project: ProjectView;
  people: PersonView[];
  onTaskSelected: (task: TaskView) => void;
  onSaveAsTemplate: () => void;
  onPrint: () => void;
  onNewTask: () => void;
  onSetRecurrence: () => void;
  onBack: () => void;
}): HTMLElement {
  const { project } = options;
  const done = completedCount(project);
  const total = project.tasks.length;
  const doing = project.tasks.filter((t) => t.status === 'doing').length;
  const remaining = total - project.tasks.filter(
    (t) => t.status === 'completed' || t.status === 'failed',
  ).length;
  const overdueTasks = project.tasks.filter((t) => t.isOverdue);
  const waitingTasks = project.tasks.filter((t) => t.status === 'waitingToAccept');
  const totalSeconds = project.tasks.reduce((sum, t) => sum + t.elapsedSeconds, 0);
  const deadline = nextDeadline(project);

  const startedAt = project.tasks
    .map((task) => task.createdAt)
    .filter((date): date is Date => date !== undefined)
    .sort((a, b) => a.getTime() - b.getTime())[0];

  function nameOf(id: string): string {
    return options.people.find((p) => p.id === id)?.fullName ?? 'Ismeretlen';
  }

  /// A terv nagy, egyben olvasható mérőszámokat mutat kártyán: címke,
  /// 30 px-es mono érték, alatta magyarázó sor. Korábban ugyanez négy
  /// címke-érték pár volt a fejlécben, magyarázat nélkül.
  function statCard(
    label: string,
    value: string,
    caption: string,
    color?: string,
  ): HTMLElement {
    return h(
      'div',
      { class: 'card pad16 col', style: { gap: '4px', minWidth: '0' } },
      h('span', { class: 'label', text: label }),
      h('span', {
        class: 'mono w7',
        style: { fontSize: '30px', lineHeight: '1.1', color: color ?? 'var(--tp)' },
        text: value,
      }),
      h('span', { class: 'caption', text: caption }),
    );
  }

  /// Címke/érték sor a jobb oldali kártyán (ugyanaz a minta, mint a W06-on).
  function detailRow(label: string, value: string): HTMLElement {
    return h(
      'div',
      {
        class: 'row g12',
        style: { alignItems: 'baseline', padding: '7px 0', borderBottom: '1px solid var(--border)' },
      },
      h('span', { class: 'caption', style: { width: '120px', flex: 'none' }, text: label }),
      h('span', {
        class: 'body-sm',
        style: { flexGrow: '1', minWidth: '0', overflowWrap: 'anywhere' },
        text: value,
      }),
    );
  }

  // Resztvevok a feladataik szamaval — a terv szerint. Ez eddig sehol nem
  // latszott: a kartyan csak az avatarok sorakoztak, terheles nelkul.
  const loadByPerson = new Map<string, number>();
  for (const task of project.tasks) {
    for (const id of task.assigneeIds) {
      loadByPerson.set(id, (loadByPerson.get(id) ?? 0) + 1);
    }
  }
  const participants = [...loadByPerson.entries()].sort((a, b) => b[1] - a[1]);

  return h(
    'div',
    { class: 'col', style: { gap: '0', height: '100%' } },
    h(
      'header',
      {
        class: 'row between g12',
        style: { padding: '16px 20px', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' },
      },
      h(
        'div',
        { class: 'row g12', style: { minWidth: '0', alignItems: 'center' } },
        h('button', {
          class: 'btn btn-ghost btn-icon btn-sm',
          type: 'button',
          'aria-label': t('project.backToList'),
          text: '‹',
          onClick: options.onBack,
        }),
        h('h2', { class: 'h2', text: project.title }),
        project.isPeriodic
          ? h(
              'span',
              { class: 'badge st-prog', style: { height: '21px', fontSize: '11px' } },
              h('span', { class: 'ic', style: { width: '11px', height: '11px' } }, icon(icons.timer, 11)),
              h('span', { text: t('project.recurring') }),
            )
          : null,
      ),
      h(
        'div',
        { class: 'row g6 wrap' },
        h('button', {
          class: 'btn btn-secondary btn-sm',
          type: 'button',
          text: t('project.recurrence'),
          onClick: options.onSetRecurrence,
        }),
        h('button', {
          class: 'btn btn-secondary btn-sm',
          type: 'button',
          text: t('project.print'),
          onClick: options.onPrint,
        }),
        h('button', {
          class: 'btn btn-secondary btn-sm',
          type: 'button',
          text: t('project.saveAsTemplate'),
          onClick: options.onSaveAsTemplate,
        }),
        h('button', {
          class: 'btn btn-primary btn-sm',
          type: 'button',
          text: t('task.newTask'),
          onClick: options.onNewTask,
        }),
      ),
    ),
    h(
      'div',
      {
        class: 'row',
        style: {
          gap: '20px',
          padding: '20px',
          alignItems: 'flex-start',
          flexGrow: '1',
          minHeight: '0',
          overflow: 'auto',
          flexWrap: 'wrap',
        },
      },
      h(
        'div',
        {
          class: 'col g14',
          // `flex-basis: 0`, kulonben a sav alapmerete a 4 kartyas racs
          // max-content szelessege lenne, es a jobb oszlop ALA csuszna.
          style: { flex: '1 1 0', minWidth: '360px', gap: '14px' },
        },
        h(
          'div',
          { class: 'grid c4' },
          statCard(
            t('project.progressLabel'),
            `${done} / ${total}`,
            t('project.percentDone', [String(Math.round(progressOf(project) * 100))]),
          ),
          statCard(
            t('filter.overdue'),
            String(overdueTasks.length),
            overdueTasks.length > 0
              ? overdueTasks
                  .slice(0, 2)
                  .map((t) => (t.sequenceNumber ? `#${t.sequenceNumber}` : t.title))
                  .join(', ')
              : t('project.noSlip'),
            overdueTasks.length > 0 ? 'var(--err)' : undefined,
          ),
          statCard(
            t('task.waitingToAccept'),
            String(waitingTasks.length),
            waitingTasks.length > 0
              ? [
                  ...new Set(waitingTasks.flatMap((t) => t.assigneeIds).map(nameOf)),
                ]
                  .slice(0, 2)
                  .join(', ')
              : 'mindenki elfogadta',
            waitingTasks.length > 0 ? 'var(--warn)' : undefined,
          ),
          statCard(
            t('report.colTracked'),
            formatElapsedClock(totalSeconds),
            t('project.onNTasks', [String(total)]),
          ),
        ),
        total === 0
          ? emptyState({
              title: t('project.noTaskYet'),
              actionLabel: t('task.newTask'),
              onAction: options.onNewTask,
            })
          : denseTaskTable({
              tasks: project.tasks,
              people: options.people,
              onSelect: options.onTaskSelected,
              firstColumnLabel: t('project.taskChain'),
            }),
      ),
      h(
        'div',
        { class: 'col g14', style: { width: '320px', flex: 'none', gap: '14px' } },
        h(
          'div',
          { class: 'card pad16 col', style: { gap: '10px' } },
          h('span', { class: 'label', text: t('project.progressLabel') }),
          h(
            'div',
            {
              class: 'prog',
              style: { height: '10px' },
              role: 'progressbar',
              'aria-valuenow': String(Math.round(progressOf(project) * 100)),
              'aria-valuemin': '0',
              'aria-valuemax': '100',
            },
            h('i', { style: { width: `${Math.round(progressOf(project) * 100)}%` } }),
          ),
          h('span', {
            class: 'caption',
            text: t('project.progressBreakdown', [
              String(done),
              String(doing),
              String(Math.max(0, remaining - doing)),
            ]),
          }),
        ),
        h(
          'div',
          { class: 'card pad16 col', style: { gap: '2px' } },
          detailRow(t('project.start'), startedAt ? formatDeadline(startedAt) : '—'),
          detailRow(t('general.deadline'), deadline ? formatDeadline(deadline) : t('project.none')),
          detailRow('Feladatok', `${total} db`),
          // A terv „Sablon" sort is mutat. A projekten nincs eltarolva,
          // melyik sablonbol keszult — a sablon csak a letrehozaskor masolodik
          // at feladatokka —, ezert az ismetlodes all itt helyette.
          detailRow(
            t('project.recurrence'),
            project.recurrenceText ??
              (project.isPeriodic ? t('project.recurringWord') : t('project.none')),
          ),
          project.description ? detailRow(t('general.description'), project.description) : null,
        ),
        h(
          'div',
          { class: 'card pad16 col', style: { gap: '10px' } },
          h('span', { class: 'label', text: t('project.participants') }),
          ...(participants.length === 0
            ? [h('span', { class: 'body-sm tm', text: t('project.nobodyAssigned') })]
            : participants.map(([id, count]) =>
                h(
                  'div',
                  { class: 'row g10', style: { alignItems: 'center' } },
                  avatar(nameOf(id), 'sm'),
                  h('span', { class: 'body-sm', text: nameOf(id) }),
                  h('span', {
                    class: 'caption',
                    style: { marginLeft: 'auto' },
                    text: `${count} feladat`,
                  }),
                ),
              )),
        ),
      ),
    ),
  );
}
